import MenuLinkItem from "@app/components/menu-link-item";
import {
  IcCheck,
  IcCircleHelp,
  IcCpu,
  IcDownload,
  IcFileText,
  IcGamepad,
  IcGlobe,
  IcHouse,
  IcIdCard,
  IcKeyRound,
  IcLogOut,
  IcMenu,
  IcMonitor,
  IcMonitorSmartphone,
  IcMoon,
  IcReceipt,
  IcServer,
  IcSun,
  IcUsers,
  IcWallet,
} from "@app/components/icons";
import { LabelAdmin, PathnameUrl } from "@app/constants";
import {
  Lang,
  ThemePref,
  usePreferences,
} from "@app/contexts/preferences.context";
import { GlobalState } from "@app/store";
import { formatMoneyVN } from "@app/utils/util";
import useLogout from "@app/utils/use-logout.hook";
import { openPortalDialog } from "@app/services/modal.service";
import ChangePasswordModal from "@app/components/change-password-modal";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";

interface NavItem {
  linkTo: string;
  label: string;
  icon: ReactNode;
}

const NAV_GROUPS: { label: string | null; items: NavItem[] }[] = [
  {
    label: null,
    items: [{ linkTo: "/home", label: "Trang chủ", icon: <IcHouse /> }],
  },
  {
    label: "Thuê máy",
    items: [
      { linkTo: PathnameUrl.GPU_HUB, label: "Thuê máy tính", icon: <IcMonitor /> },
      { linkTo: PathnameUrl.CLOUD_RENTAL, label: LabelAdmin.CLOUD_RENTAL, icon: <IcGamepad /> },
      { linkTo: PathnameUrl.VPS_RENTAL, label: "Thuê VPS", icon: <IcServer /> },
      { linkTo: PathnameUrl.PC_COMPONENT, label: "Thuê linh kiện mang về", icon: <IcCpu /> },
    ],
  },
  {
    label: "Đơn hàng",
    items: [
      { linkTo: PathnameUrl.ORDER_MANAGEMENT, label: LabelAdmin.ORDER_MANAGEMENT, icon: <IcReceipt /> },
      { linkTo: PathnameUrl.IDENTITY_VERIFICATION, label: LabelAdmin.IDENTITY_VERIFICATION, icon: <IcFileText /> },
    ],
  },
  {
    label: "Tài khoản",
    items: [
      { linkTo: PathnameUrl.RECHARGE, label: "Nạp tiền", icon: <IcWallet /> },
      { linkTo: PathnameUrl.PROFILE, label: LabelAdmin.EKYC, icon: <IcIdCard /> },
      { linkTo: PathnameUrl.REF_CODE, label: LabelAdmin.REF_CODE, icon: <IcUsers /> },
    ],
  },
  {
    label: "Hỗ trợ",
    items: [
      {
        linkTo:
          "https://docs.google.com/forms/d/1soWtWMAm3_SgN6oe0PCDeHdojvjYMuIeYGNTRKE0ZUo/preview",
        label: LabelAdmin.DOWNLOAD_GAME,
        icon: <IcDownload />,
      },
      { linkTo: PathnameUrl.USER_GUIDE_MANAGEMENT, label: "Hướng dẫn sử dụng", icon: <IcCircleHelp /> },
    ],
  },
];

const THEMES: [ThemePref, ReactNode, string][] = [
  ["light", <IcSun key="l" />, "Sáng"],
  ["dark", <IcMoon key="d" />, "Tối"],
  ["system", <IcMonitorSmartphone key="s" />, "Hệ thống"],
];

const LANGS: [Lang, string][] = [
  ["vi", "Tiếng Việt"],
  ["en", "English"],
];

type PopKind = "lang" | "theme" | "user" | null;

function MainLayout() {
  const [pop, setPop] = useState<PopKind>(null);
  const [isShowSidebar, setIsShowSidebar] = useState(false);

  const { myInfo } = useSelector(selectMyInfo);
  const { t, theme, setTheme, lang, setLang, isDark } = usePreferences();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const popRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(popRef, () => setPop(null));

  useEffect(() => {
    if (!pop && !isShowSidebar) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPop(null);
        setIsShowSidebar(false);
      }
    };

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [pop, isShowSidebar]);

  const logout = useLogout();

  const onMenuItemClick = () => {
    setIsShowSidebar(false);
  };

  const togglePop = (kind: PopKind) => setPop((p) => (p === kind ? null : kind));

  // Hợp đồng và xác minh danh tính chỉ hiện khi đã eKYC; riêng trang hồ sơ
  // đổi thành "Xác minh Số Điện Thoại" khi tài khoản đang được yêu cầu xác minh SĐT.
  const resolveItem = (item: NavItem): NavItem | null => {
    const gated =
      item.linkTo === PathnameUrl.IDENTITY_VERIFICATION ||
      item.linkTo === PathnameUrl.PROFILE;

    if (gated && myInfo.kyc === 0) {
      if (
        item.linkTo === PathnameUrl.PROFILE &&
        myInfo.isRequestVerifyPhone === 1
      ) {
        return { ...item, label: "Xác minh Số Điện Thoại" };
      }

      return null;
    }

    return item;
  };

  const current = NAV_GROUPS.flatMap((g) => g.items).find((i) =>
    i.linkTo === "/" ? pathname === "/" : pathname.startsWith(i.linkTo)
  );

  const initial = (myInfo.username || "?").charAt(0).toUpperCase();

  const prefsPicker = (
    <>
      <div className="px-1.5 text-[11px] font-semibold uppercase tracking-[.08em] text-faint">
        {t("Giao diện")}
      </div>
      <div className="flex w-full gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
        {THEMES.map(([key, icon, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={theme === key}
            onClick={() => setTheme(key)}
            className={clsx(
              "flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-[7px] px-1 py-[7px] text-xs font-semibold",
              theme === key ? "bg-surface text-fg shadow-card" : "text-muted"
            )}
          >
            <span className="text-[14px]">{icon}</span>
            {t(label)}
          </button>
        ))}
      </div>
      <div className="px-1.5 text-[11px] font-semibold uppercase tracking-[.08em] text-faint">
        {t("Ngôn ngữ")}
      </div>
      <div className="flex w-full gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
        {LANGS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            lang={key}
            aria-pressed={lang === key}
            onClick={() => setLang(key)}
            className={clsx(
              "flex flex-1 items-center justify-center rounded-[7px] px-1 py-[7px] text-[12.5px] font-semibold",
              lang === key ? "bg-surface text-fg shadow-card" : "text-muted"
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );

  const popItem =
    "flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] text-fg hover:bg-surface-2";

  return (
    <div className="grid min-h-screen grid-cols-[220px_minmax(0,1fr)] max-[900px]:grid-cols-1">
      {/* ---------- sidebar ---------- */}
      <aside
        aria-label={t("Menu chính")}
        className={clsx(
          "sticky top-0 flex h-screen flex-col gap-[18px] overflow-y-auto border-r border-line bg-surface px-3 pb-6 pt-4",
          "max-[900px]:fixed max-[900px]:bottom-0 max-[900px]:left-0 max-[900px]:z-[70] max-[900px]:h-auto max-[900px]:w-[280px] max-[900px]:transition-transform",
          isShowSidebar
            ? "max-[900px]:translate-x-0 max-[900px]:shadow-[10px_0_40px_rgba(0,0,0,.25)]"
            : "max-[900px]:-translate-x-[105%]"
        )}
      >
        <button
          type="button"
          className="flex items-center gap-2 px-2 pb-1.5 pt-1 text-[20px] font-bold tracking-[-.01em] text-fg"
          onClick={() => navigate(PathnameUrl.GPU_HUB)}
        >
          <span className="rounded-[7px] bg-brand px-[7px] py-px text-[17px] text-brand-ink">
            PC
          </span>
          Render
        </button>

        <nav className="flex flex-col gap-[18px]">
          {NAV_GROUPS.map((group, gi) => {
            const items = group.items
              .map(resolveItem)
              .filter((i): i is NavItem => !!i);

            if (!items.length) return null;

            return (
              <div key={gi} className="flex flex-col gap-0.5">
                {group.label && (
                  <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[.08em] text-faint">
                    {t(group.label)}
                  </div>
                )}
                {items.map((item) => (
                  <MenuLinkItem
                    key={item.linkTo}
                    linkTo={item.linkTo}
                    label={t(item.label)}
                    icon={item.icon}
                    onMenuItemClick={onMenuItemClick}
                  />
                ))}
              </div>
            );
          })}
        </nav>

        <div className="mt-auto hidden flex-col gap-2 border-t border-line px-1 pt-3.5 max-[760px]:flex">
          {prefsPicker}
        </div>
      </aside>

      {isShowSidebar && (
        <div
          className="fixed inset-0 z-[65] bg-[rgba(5,10,20,.45)] min-[901px]:hidden"
          onClick={() => setIsShowSidebar(false)}
        />
      )}

      {/* ---------- main ---------- */}
      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-20 flex h-top items-center gap-3 border-b border-line px-6 backdrop-blur-[10px] max-[900px]:px-4 [background:color-mix(in_srgb,var(--bg)_88%,transparent)]">
          <button
            type="button"
            aria-label={t("Mở menu")}
            className="hidden h-[38px] w-[38px] place-items-center rounded-[10px] border border-line bg-surface text-[18px] text-fg max-[900px]:grid"
            onClick={() => setIsShowSidebar(true)}
          >
            <IcMenu />
          </button>

          {current && (
            <div className="text-[13px] text-muted max-[900px]:hidden">
              PCRender / <b className="font-semibold text-fg">{t(current.label)}</b>
            </div>
          )}

          <span className="flex-1" />

          <div className="flex flex-col items-end leading-[1.2]">
            <small className="text-[11.5px] text-muted max-[760px]:hidden">
              {t("Số dư")}
            </small>
            <strong className="text-[15px] tabular-nums text-fg">
              {formatMoneyVN(myInfo.balance)} đ
            </strong>
          </div>

          <button
            type="button"
            className="inline-flex h-[38px] items-center justify-center rounded-[9px] bg-brand px-3.5 text-[13.5px] font-semibold text-brand-ink transition-colors hover:bg-brand-hover max-[760px]:px-3"
            onClick={() => navigate(PathnameUrl.RECHARGE)}
          >
            {t("Nạp tiền")}
          </button>

          <div ref={popRef} className="flex items-center gap-3">
            <div className="relative max-[760px]:hidden">
              <button
                type="button"
                aria-label={t("Ngôn ngữ")}
                title={t("Ngôn ngữ")}
                aria-haspopup="menu"
                aria-expanded={pop === "lang"}
                onClick={() => togglePop("lang")}
                className="inline-flex h-[38px] items-center gap-1.5 rounded-[9px] border border-line bg-surface px-2.5 text-[13px] font-semibold text-fg hover:border-line-2"
              >
                <IcGlobe className="text-[16px]" />
                {lang.toUpperCase()}
              </button>
              {pop === "lang" && (
                <div className="absolute right-0 top-[46px] z-[60] min-w-[180px] rounded-[10px] border border-line bg-surface p-1.5 shadow-[0_10px_30px_rgba(0,0,0,.18)]">
                  {LANGS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={popItem}
                      onClick={() => {
                        setLang(key);
                        setPop(null);
                      }}
                    >
                      <span className="w-[22px] text-[11px] font-bold text-muted">
                        {key.toUpperCase()}
                      </span>
                      <span lang={key}>{label}</span>
                      {lang === key && <IcCheck className="ml-auto text-brand" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative max-[760px]:hidden">
              <button
                type="button"
                aria-label={t("Giao diện")}
                title={t("Giao diện")}
                aria-haspopup="menu"
                aria-expanded={pop === "theme"}
                onClick={() => togglePop("theme")}
                className="grid h-[38px] w-[38px] place-items-center rounded-[10px] border border-line bg-surface text-[18px] text-fg hover:border-line-2"
              >
                {theme === "system" ? (
                  <IcMonitorSmartphone />
                ) : isDark ? (
                  <IcMoon />
                ) : (
                  <IcSun />
                )}
              </button>
              {pop === "theme" && (
                <div className="absolute right-0 top-[46px] z-[60] min-w-[180px] rounded-[10px] border border-line bg-surface p-1.5 shadow-[0_10px_30px_rgba(0,0,0,.18)]">
                  {THEMES.map(([key, icon, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={popItem}
                      onClick={() => {
                        setTheme(key);
                        setPop(null);
                      }}
                    >
                      <span className="text-[16px]">{icon}</span>
                      {t(label)}
                      {theme === key && <IcCheck className="ml-auto text-brand" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={pop === "user"}
                onClick={() => togglePop("user")}
                className="flex items-center gap-2 rounded-[10px] p-1 font-semibold text-fg"
              >
                <span className="grid h-[34px] w-[34px] place-items-center rounded-full bg-brand-soft text-[13px] font-bold text-brand">
                  {initial}
                </span>
                <span className="max-[760px]:hidden">{myInfo.username}</span>
              </button>
              {pop === "user" && (
                <div className="absolute right-0 top-[50px] z-[60] min-w-[220px] rounded-[10px] border border-line bg-surface p-1.5 shadow-[0_10px_30px_rgba(0,0,0,.18)]">
                  <div className="mb-1 flex items-center gap-2.5 border-b border-line px-2.5 pb-2.5 pt-2">
                    <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full bg-brand-soft text-[13px] font-bold text-brand">
                      {initial}
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <b className="font-mono text-sm text-fg">{myInfo.username}</b>
                      {myInfo.fullName && (
                        <small className="max-w-[190px] truncate text-xs uppercase text-muted">{myInfo.fullName}</small>
                      )}
                      {myInfo.email && (
                        <small className="max-w-[190px] truncate font-mono text-xs text-muted">{myInfo.email}</small>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={popItem}
                    onClick={() => {
                      setPop(null);
                      navigate(PathnameUrl.PROFILE);
                    }}
                  >
                    <IcIdCard className="text-[16px]" />
                    <span>
                      {t("Tài khoản")}
                      <small className="block text-[11.5px] font-normal text-faint">
                        {t("Thông tin và xác minh danh tính")}
                      </small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={popItem}
                    onClick={() => {
                      setPop(null);
                      openPortalDialog(ChangePasswordModal, {
                        username: myInfo.username,
                        email: myInfo.email,
                      });
                    }}
                  >
                    <IcKeyRound className="text-[16px]" />
                    {t("Đổi mật khẩu")}
                  </button>
                  <hr className="mx-0.5 my-1 border-line" />
                  <button
                    type="button"
                    className={clsx(popItem, "!text-bad")}
                    onClick={logout}
                  >
                    <IcLogOut className="text-[16px]" />
                    {t("Đăng xuất")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const selectMyInfo = (state: GlobalState) => state.myInfo;

export default MainLayout;
